import React, { useMemo } from 'react';
import { TableRow, TableCell, Box } from '@mui/material';
import dayjs from 'dayjs';
import { formatCurrency } from '../../utils/formatUtils';
import type { AuditItem } from '../../types/features/storeDetails/legacyAudits';
import { getStoreConfigurationFlags } from './utils/legacyAuditStoreConfiguration';
import { transformAuditToAuditItem } from './utils/legacyAuditTransformers';
import LegacyAuditChildRowConditionalColumns from './components/LegacyAuditChildRowConditionalColumns';

interface LegacyChildRowsProps {
  audits: any[];
  selectedStore?: any;
  tableColumnCount: number;
  onAuditDetails?: (item: AuditItem) => void;
  hasPosType?: boolean;
  parentRowBackgroundColor: string;
}

const LegacyChildRows: React.FC<LegacyChildRowsProps> = ({
  audits,
  selectedStore,
  tableColumnCount,
  onAuditDetails,
  hasPosType = true,
  parentRowBackgroundColor,
}) => {
  // Extract conditional logic to top level for better readability
  const { integrationCase } = getStoreConfigurationFlags(selectedStore);

  const handleAudit = (event: React.MouseEvent, item: AuditItem) => {
    event.stopPropagation();
    if (onAuditDetails) {
      onAuditDetails(item);
    }
  };

  // Transform audits array to AuditItem format and filter
  const auditData = useMemo(() => {
    if (!audits || !Array.isArray(audits)) {
      return [];
    }
    
    return audits
      .map(transformAuditToAuditItem)
      .filter((item: AuditItem) => item?.newly_activated_count || item?.total_sold || item?.moved_to_backstock_count);
  }, [audits]);

  let tableCellStyle = {
    textAlign: 'right',
    fontSize: '14px',
    fontWeight: 400,
  }
  const groupBorderStyle = { borderRight: '1px solid #A3AED0CC' };
  const innerBorderStyle = { borderRight: '1px solid #A3AED03D' };
  const childRowHeight = 54;

      return (
        <>
          {auditData.length === 0 ? (
        <TableRow sx={{ bgcolor: parentRowBackgroundColor }}>
          <TableCell colSpan={tableColumnCount}>
            <p style={{ textAlign: 'center', margin: 0 }}>No data found</p>
          </TableCell>
        </TableRow>
      ) : (
        auditData.map((item: AuditItem, rowIdx: number) => {
          const rowBackgroundColor = parentRowBackgroundColor;
          return (
            <TableRow
              key={rowIdx}
              sx={{
                bgcolor: rowBackgroundColor,
                height: `${childRowHeight}px`,
                '& .MuiTableCell-root': {
                  py: 0,
                  height: `${childRowHeight}px`,
                  verticalAlign: 'middle',
                },
              }}
            >
              {/* Column 0: Date/Time */}
              <TableCell
                sx={{
                  width: 280,
                  minWidth: 280,
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  bgcolor: rowBackgroundColor,
                  ...groupBorderStyle,
                  textAlign: 'right',
                }}
              >
                <Box
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    paddingRight: '30px',
                    color: "#222B4599",
                    height: `${childRowHeight}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {item?.report_datetime && item?.user_name ? (
                    <span>
                      {`${item?.user_name} @ `}
                      <span
                        style={{ color: '#2C6CA0', textDecoration: 'underline', cursor: 'pointer',  }}
                        onClick={(event) => handleAudit(event, item)}
                      >
                        {dayjs(item?.report_datetime).format('hh:mm A')}
                      </span>
                    </span>
                  ) : null}
                </Box>
              </TableCell>

              {/* Column 1: # of Audits */}
              <TableCell sx={{ ...tableCellStyle, ...groupBorderStyle }}>
                {item?.audits}
              </TableCell>

              {/* Column 2: Opening Stock */}
              <TableCell sx={{ ...tableCellStyle, ...innerBorderStyle }}>
                {item?.opening_count ?? 0}
              </TableCell>

              {/* Column 3: Packs Activated */}
              <TableCell sx={{ ...tableCellStyle, ...innerBorderStyle }}>
                {item?.newly_activated_count ?? 0}
              </TableCell>

              {/* Column 4: Sales */}
              <TableCell sx={{ ...tableCellStyle, ...innerBorderStyle }}>
                {item?.total_sold ?? 0}
              </TableCell>

              {/* Column 5: Packs moved-to backstock */}
              <TableCell sx={{ ...tableCellStyle, ...innerBorderStyle }}>
                {item?.moved_to_backstock_count ?? 0}
              </TableCell>

              {/* Column 5: Closing Stock */}
              <TableCell sx={{ ...tableCellStyle, ...innerBorderStyle }}>
                {item?.closing_count ?? 0}
              </TableCell>

              {/* Column 6: Closing Value */}
              <TableCell sx={{ textAlign: 'right', ...groupBorderStyle }}>
                {formatCurrency(item?.revenue ?? 0)}
              </TableCell>

              {/* Conditional Columns */}
              <LegacyAuditChildRowConditionalColumns
                item={item}
                integrationCase={integrationCase}
                hasPosType={hasPosType}
                groupBorderColor="#A3AED0CC"
                innerBorderColor="#A3AED03D"
              />

              {/* Column 12: Reconcile */}
              <TableCell sx={{ textAlign: 'center' }}>
                {/* Empty cell for reconcile column in child rows */}
              </TableCell>
            </TableRow>
          );
        })
      )}
    </>
  );
};

export default LegacyChildRows;
